import React from 'react';
import {classes, stylesheet} from 'typestyle';
import {observer} from 'mobx-react';
import {important} from 'csx';
import {Draggable, Droppable} from 'react-beautiful-dnd';
import {Draggables} from '../../beautiful-dnd/Draggable';
import PaperSheetTextBlock from './PaperSheetBlocks/PaperSheetTextBlock';
import {DiagramInstruction} from '../../stores/OdtExportStore';
import DiagramBuildingBlockDroppable from './PaperSheetBlocks/DiagramBuildingBlockDroppable';
import DiagramTableBuildingBlockDroppable from './PaperSheetBlocks/DiagramTableBuildingBlockDroppable';
import {NumericFormatter} from '../../utility/FormatValueUtils';
import {Channel} from '../../types/Channel';
import TableBuildingBlockDroppable from './PaperSheetBlocks/TableBuildingBlockDroppable';
import {DataKey} from '../../stores/GeneralSelectorTableStore';
import {
    BUILDING_BLOCK_TYPE_COLORS,
    BuildingBlockType,
    MergedBuildingBlockInstructions,
    NamedDateRangeWithPreviewDateRange,
    ReportBuildingBlock,
} from '../types/Report';
import {REPORT_DROPABLES} from '../../beautiful-dnd/Droppable';
import BuildingBlockHeader from './PaperSheetBlocks/BuildingBlockHeader';
import {GreyBlueBackgroundColor, GreyColor, Shadow1, WhiteColor} from '../../styles/colors';
import {DateIdBundle} from '../stores/ReportEditorStore';
import {I18n} from '../../i18n/i18n';

interface PaperSheetProps {
    targetCurrency?: string;
    updateTableColumnVisibility: (idTableBuildingBlock: string, dataKey: DataKey, visibility: boolean) => void;
    updateLineLimitForTableBlock: (idTableBuildingBlock: string, lineLimit: number) => void;
    reportBuildingBlocks: ReportBuildingBlock[];
    removeBuildingBlock: (buildingBlockId: string) => void;
    removeDiagramTraitFromBuildingBlock: (idBuildingBlock: string, diagramTrait: DiagramInstruction) => void;
    getMergedBuildingBlockInstructions: (buildingBlock: ReportBuildingBlock) => MergedBuildingBlockInstructions;
    removeTableTraitFromBuildingBlock: (idBuildingBlock: string, traitKey: string, traitToRemove: any) => void;
    moneyFormatToTargetCurrency: (fractions?: number) => NumericFormatter;
    getFullChannelForChannelName: (channelName: string) => Channel;
    changeBuildingBlockType: (idBuildingBlock: string, newType: BuildingBlockType) => void;
    namedDateRanges: NamedDateRangeWithPreviewDateRange[];
    changeSelectedNamedDateRange: (idBuildingBlock: string, dateIdBundle: DateIdBundle) => void;
    checkIfIdBelongsToBuildingBlock: (id?: string, defaultValue?: boolean) => boolean;
    allowBlockCombining: boolean;
    traitChecks: {
        hasToManyChannels: (reportBlocks: ReportBuildingBlock[]) => boolean;
        hasToManySearches: (reportBlocks: ReportBuildingBlock[]) => boolean;
        hasToManySortings: (reportBlocks: ReportBuildingBlock[]) => boolean;
        reportBuildingBlocksAreEmpty: (buildingBlocks: ReportBuildingBlock[]) => boolean;
        hasSearchProductMixedBuildingBlocks: (reportBlocks: ReportBuildingBlock[]) => boolean;
    };
    removeDiagramCompareTraitFromBuildingBlock: (idBuildingBlock: string) => void;
}

@observer
export default class PaperSheet extends React.Component<PaperSheetProps> {
    private getSelectedNameDateRange(idSelectedNamedDateRange?: string): NamedDateRangeWithPreviewDateRange | undefined {
        return this.props.namedDateRanges.find((namedDateRange) => namedDateRange.id === idSelectedNamedDateRange);
    }

    private renderBuildingBlockBody = (buildingBlock: ReportBuildingBlock, customBuildingBlockRemoval?: (idBlock: string) => void) => {
        const {
            removeDiagramTraitFromBuildingBlock,
            removeTableTraitFromBuildingBlock,
            moneyFormatToTargetCurrency,
            getFullChannelForChannelName,
            removeDiagramCompareTraitFromBuildingBlock,
        } = this.props;
        const removeBuildingBlock = customBuildingBlockRemoval ? customBuildingBlockRemoval : this.props.removeBuildingBlock;
        switch (buildingBlock.blockType) {
            case BuildingBlockType.TEXT:
                return (
                    <PaperSheetTextBlock
                        changeBuildingBlockType={this.props.changeBuildingBlockType}
                        removeBuildingBlock={removeBuildingBlock}
                        buildingBlock={buildingBlock}
                    />
                );
            case BuildingBlockType.TABLE:
                return (
                    <TableBuildingBlockDroppable
                        targetCurrency={this.props.targetCurrency}
                        traitChecks={this.props.traitChecks}
                        namedDateRangePackage={{
                            namedDateRanges: this.props.namedDateRanges,
                            selectedIdNamedDateRange: buildingBlock.idDate,
                            changeSelectedNamedDateRange: this.props.changeSelectedNamedDateRange,
                        }}
                        selectedNamedDateRange={this.getSelectedNameDateRange(buildingBlock.idDate)}
                        changeBuildingBlockType={this.props.changeBuildingBlockType}
                        updateTableColumnVisibility={this.props.updateTableColumnVisibility}
                        updateLineLimitForTableBlock={this.props.updateLineLimitForTableBlock}
                        getMergedBuildingBlockInstructions={this.props.getMergedBuildingBlockInstructions}
                        removeDiagramTraitFromBuildingBlock={removeDiagramTraitFromBuildingBlock}
                        removeTableTraitFromBuildingBlock={removeTableTraitFromBuildingBlock}
                        removeBuildingBlock={removeBuildingBlock}
                        buildingBlock={buildingBlock}
                        moneyFormatToTargetCurrency={moneyFormatToTargetCurrency}
                        getFullChannelForChannelName={getFullChannelForChannelName}
                        removeDiagramCompareTraitFromBuildingBlock={removeDiagramCompareTraitFromBuildingBlock}
                    />
                );
            case BuildingBlockType.DIAGRAM:
                return (
                    <DiagramBuildingBlockDroppable
                        targetCurrency={this.props.targetCurrency}
                        traitChecks={this.props.traitChecks}
                        namedDateRangePackage={{
                            namedDateRanges: this.props.namedDateRanges,
                            selectedIdNamedDateRange: buildingBlock.idDate,
                            selectedIdNamedDateCompareRange: buildingBlock.idDateCompare,
                            changeSelectedNamedDateRange: this.props.changeSelectedNamedDateRange,
                        }}
                        selectedNamedDateRange={this.getSelectedNameDateRange(buildingBlock.idDate)}
                        selectedNamedCompareDateRange={this.getSelectedNameDateRange(buildingBlock.idDateCompare)}
                        changeBuildingBlockType={this.props.changeBuildingBlockType}
                        getMergedBuildingBlockInstructions={this.props.getMergedBuildingBlockInstructions}
                        removeDiagramTraitFromBuildingBlock={removeDiagramTraitFromBuildingBlock}
                        removeTableTraitFromBuildingBlock={removeTableTraitFromBuildingBlock}
                        removeBuildingBlock={removeBuildingBlock}
                        buildingBlock={buildingBlock}
                        getFullChannelForChannelName={getFullChannelForChannelName}
                        removeDiagramCompareTraitFromBuidingBlock={removeDiagramCompareTraitFromBuildingBlock}
                    />
                );
            case BuildingBlockType.DIAGRAM_TABLE:
                return (
                    <DiagramTableBuildingBlockDroppable
                        targetCurrency={this.props.targetCurrency}
                        traitChecks={this.props.traitChecks}
                        namedDateRangePackage={{
                            namedDateRanges: this.props.namedDateRanges,
                            selectedIdNamedDateRange: buildingBlock.idDate,
                            selectedIdNamedDateCompareRange: buildingBlock.idDateCompare,
                            changeSelectedNamedDateRange: this.props.changeSelectedNamedDateRange,
                        }}
                        selectedNamedDateRange={this.getSelectedNameDateRange(buildingBlock.idDate)}
                        selectedNamedCompareDateRange={this.getSelectedNameDateRange(buildingBlock.idDateCompare)}
                        updateTableColumnVisibility={this.props.updateTableColumnVisibility}
                        updateLineLimitForTableBlock={this.props.updateLineLimitForTableBlock}
                        changeBuildingBlockType={this.props.changeBuildingBlockType}
                        getMergedBuildingBlockInstructions={this.props.getMergedBuildingBlockInstructions}
                        removeDiagramTraitFromBuildingBlock={removeDiagramTraitFromBuildingBlock}
                        removeTableTraitFromBuildingBlock={removeTableTraitFromBuildingBlock}
                        removeBuildingBlock={removeBuildingBlock}
                        buildingBlock={buildingBlock}
                        moneyFormatToTargetCurrency={moneyFormatToTargetCurrency}
                        getFullChannelForChannelName={getFullChannelForChannelName}
                        removeDiagramCompareTraitFromBuildingBlock={removeDiagramCompareTraitFromBuildingBlock}
                    />
                );
            case BuildingBlockType.PAGE_BREAK:
                return (
                    <BuildingBlockHeader
                        idBuildingBlock={buildingBlock.id}
                        changeBuildingBlockType={this.props.changeBuildingBlockType}
                        removeBuildingBlock={() => removeBuildingBlock(buildingBlock.id)}
                        buildingBlockType={buildingBlock.blockType}
                    />
                );
            default:
                throw Error('Unsupported building block type');
        }
    };

    private renderReportBuildingBlock = (buildingBlock: ReportBuildingBlock, index: number) => {
        const {id} = buildingBlock;
        return (
            <Draggable key={id} draggableId={id} index={index}>
                {(provided, {combineTargetFor}) => {
                    return (
                        <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={classes(
                                paperCss.blockContainer,
                                typeof combineTargetFor === 'string' && !this.props.checkIfIdBelongsToBuildingBlock(combineTargetFor, true)
                                    ? 'combineHovered'
                                    : undefined
                            )}>
                            <div
                                className={paperCss.grabHandle}
                                style={{backgroundColor: BUILDING_BLOCK_TYPE_COLORS[buildingBlock.blockType].toString()}}
                            />
                            <div className={paperCss.blockBody}>{this.renderBuildingBlockBody(buildingBlock)}</div>
                        </div>
                    );
                }}
            </Draggable>
        );
    };

    private renderEditorInstructions(): React.ReactNode {
        return (
            <div className={paperCss.introductionBody}>
                <div>
                    <I18n i18nKey="report:editorIntroduction" />
                </div>
            </div>
        );
    }

    public render() {
        return (
            <Droppable
                isCombineEnabled={this.props.allowBlockCombining}
                droppableId={REPORT_DROPABLES.REPORT_PAPERSHEET}
                type={Draggables.ONLY_DROPPABLE_ON_REPORT_PAPER}>
                {(provided, snapshot) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={classes(paperCss.paper, snapshot.isDraggingOver ? paperCss.paperHover : undefined)}>
                        {this.props.reportBuildingBlocks.length === 0 ? this.renderEditorInstructions() : null}
                        {this.props.reportBuildingBlocks.map(this.renderReportBuildingBlock)}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        );
    }
}

const paperCss = stylesheet({
    paperHover: {
        backgroundColor: important(GreyBlueBackgroundColor),
    },
    combineHovered: {
        backgroundColor: GreyBlueBackgroundColor,
    },
    paper: {
        transition: '.2s',
        overflow: 'auto',
        padding: '15px 15px 0px 15px',
        backgroundColor: WhiteColor,
        height: '100%',
        width: '100%',
        boxShadow: Shadow1,
    },
    blockContainer: {
        justifyContent: 'center',
        marginBottom: 15,
        cursor: 'move',
        backgroundColor: WhiteColor,
        display: 'flex',
        boxShadow: Shadow1,
        $nest: {
            '&.combineHovered': {
                backgroundColor: GreyBlueBackgroundColor,
            },
        },
    },
    grabHandle: {
        minWidth: 16,
    },
    blockBody: {
        flexGrow: 1,
        width: 1,
    },
    introductionBody: {
        color: GreyColor,
        width: '60%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        margin: 'auto',
    },
});



// WEBPACK FOOTER //
// ./src/report/views/PaperSheet.tsx